/**
 * Whether a member needs something done about them.
 *
 * Replaces a Status column that printed a state — "Paid · active" — on every row
 * whether or not it called for anything. A column that says "fine" nine times
 * out of ten is a column nobody reads, and the one row that did need attention
 * looked like all the others.
 *
 * This answers the different question: is there an action here, and what is it.
 * Green rows are the ones with nothing to do, so an administrator scanning the
 * table is looking for the two that are not green rather than reading eight
 * that are.
 *
 * Pure, and shared, so the same rules produce the badge, its tooltip, and any
 * count built from them. A second implementation would drift.
 */

export type MemberAttentionLevel = "ok" | "warning" | "critical";

export type MemberAttention = {
  level: MemberAttentionLevel;
  /** Every reason, most urgent first. Empty when nothing needs doing. */
  reasons: string[];
  /** One line for a badge or a tooltip's first row. */
  summary: string;
};

export type MemberAttentionInput = {
  /** The membership entitlement's current state. */
  entitlementState: "inactive" | "active" | "past_due" | "cancelled" | "expired" | null;
  paidStatus: "paid" | "non_paid";
  /** Admin access may be granted without a Stripe subscription or renewal date. */
  isSiteAdmin?: boolean;
  /** When the paid period next turns over, or ends if cancelling. */
  currentPeriodEnd: Date | string | null;
  /** Billing stops at `currentPeriodEnd` rather than renewing. */
  cancelAtPeriodEnd: boolean;
  /**
   * When the card on file stops being valid — the last day of its expiry month.
   *
   * Only the date is carried, never the card. The billing view states that no
   * card data is stored or rendered, and "this payment method expires before the
   * renewal" is the actionable part of it; the number and the expiry itself are
   * Stripe's to hold and to show.
   */
  paymentMethodExpiresAt?: Date | string | null;
  /** No usable payment method is attached to the subscription at all. */
  missingPaymentMethod?: boolean;
};

function asDate(value: Date | string | null | undefined) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDay(date: Date) {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
}

/** Renewals inside this window are close enough that a broken card matters now. */
const RENEWAL_SOON_DAYS = 30;

export function memberAttention(input: MemberAttentionInput, now = new Date()): MemberAttention {
  const critical: string[] = [];
  const warning: string[] = [];

  const renewsAt = asDate(input.currentPeriodEnd);
  const cardExpiresAt = asDate(input.paymentMethodExpiresAt);
  const daysUntilRenewal = renewsAt
    ? Math.ceil((renewsAt.getTime() - now.getTime()) / 86_400_000)
    : null;

  if (input.entitlementState === "past_due") {
    critical.push("A membership payment failed. Stripe will retry, then cancel — the member may need a new card.");
  }

  if (input.entitlementState === "expired") {
    critical.push("The membership has expired and access has lapsed.");
  }

  if (input.entitlementState === "cancelled") {
    warning.push("The membership was cancelled.");
  }

  /*
   * A card that has already lapsed on a live membership is a failure that has
   * not happened yet — the next renewal attempt will decline. Worth raising
   * before the payment fails rather than after.
   */
  if (input.paidStatus === "paid" && cardExpiresAt && cardExpiresAt.getTime() < now.getTime()) {
    critical.push("The payment method on file has expired. The next renewal will fail until it is replaced.");
  } else if (
    input.paidStatus === "paid" &&
    cardExpiresAt &&
    renewsAt &&
    cardExpiresAt.getTime() < renewsAt.getTime()
  ) {
    warning.push(`The payment method on file expires before the ${formatDay(renewsAt)} renewal.`);
  }

  if (input.paidStatus === "paid" && input.missingPaymentMethod) {
    warning.push("No payment method is attached to this subscription.");
  }

  /*
   * Deliberately not raised as an attention reason.
   *
   * The Renews on column says "Cancels — does not renew" beside the date it
   * applies to, which is where an administrator reads it. Repeating it here
   * turned every cancelling member amber, so a set-to-cancel membership — an
   * ordinary, intended thing — looked like the same class of problem as a failed
   * payment. The countdown below still applies when the date is close.
   */

  /*
   * A paid membership with no renewal date is not an alarm on its own; it can
   * simply be a subscription Stripe has not reported yet. It is worth noting
   * only because every other paid row has one.
   */
  if (input.paidStatus === "paid" && !input.isSiteAdmin && !renewsAt && input.entitlementState === "active") {
    warning.push("No renewal date could be found for this membership in Stripe.");
  }

  if (
    input.paidStatus === "paid" &&
    typeof daysUntilRenewal === "number" &&
    daysUntilRenewal >= 0 &&
    daysUntilRenewal <= RENEWAL_SOON_DAYS &&
    input.cancelAtPeriodEnd
  ) {
    warning.push(`Access ends in ${daysUntilRenewal} ${daysUntilRenewal === 1 ? "day" : "days"}.`);
  }

  const reasons = [...critical, ...warning];

  /*
   * The label says what kind of account it is and how it is doing, in that
   * order, because those are the two things an administrator scanning the column
   * wants and they are read together: "Paid Member — Active", "Paid Member —
   * Payment Failure".
   *
   * Deliberately short. The full explanation lives in the tooltip and on the
   * member's own row when opened; a label long enough to hold it would wrap and
   * push every row taller than its neighbours.
   */
  const kind = input.paidStatus === "paid" || input.entitlementState === "past_due"
    ? "Paid Member"
    : "Free Account";

  /*
   * The badge says only what kind of account this is. Colour carries the state —
   * green in good standing, amber a problem coming, red one that has happened —
   * and the reason is in the tooltip and on the Billing tab.
   *
   * Appending "— Payment Failure" made the widest badge nearly twice the width of
   * the narrowest, so the column sized itself to the worst case and the table
   * grew for the sake of words the colour was already saying.
   */
  if (critical.length > 0) return { level: "critical", reasons, summary: kind };
  if (warning.length > 0) return { level: "warning", reasons, summary: kind };
  return { level: "ok", reasons: [], summary: kind };
}
